<template>
  <div class="barcode-scanner-wrapper p-3">
    <!-- USB ready indicator. lastScanned is purely a display echo of the
         last successfully-parsed scan — never the scanner buffer itself,
         and rendered in its own element (never string-concatenated with the
         status label) so it can never visually read as if the label text
         were part of the scanned value. -->
    <div class="usb-indicator" :class="{ 'usb-flash': flashActive }">
      <span class="usb-dot"></span>
      <span>ماسح USB جاهز</span>
    </div>
    <div v-if="lastScanned" class="last-scanned mt-1 text-success fw-bold small">
      آخر مسح: {{ lastScanned }}
    </div>

    <!-- Camera toggle -->
    <button
      class="btn btn-sm mt-2"
      :class="cameraScanning ? 'btn-danger' : 'btn-outline-secondary'"
      @click="toggleCamera"
    >
      <i class="pi" :class="cameraScanning ? 'pi-times' : 'pi-camera'"></i>
      {{ cameraScanning ? 'إغلاق الكاميرا' : 'تشغيل الكاميرا' }}
    </button>

    <div v-if="cameraScanning" class="camera-viewport mt-2">
      <div :id="scannerDivId"></div>
    </div>

    <p v-if="errorMsg" class="text-danger mt-1 mb-0 small">{{ errorMsg }}</p>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';

// Maps a KeyboardEvent.code (the PHYSICAL key pressed, independent of the
// OS's active keyboard layout AND its Caps Lock state) to the US-QWERTY
// ASCII character it produces. A USB HID keyboard-wedge scanner "presses"
// physical keys as if a US layout were active; if Windows is actually
// running an Arabic layout, the OS translates those same physical keys into
// Arabic characters before the browser ever sees them via `event.key`.
// `event.code` is layout-independent and lets us recover the intended ASCII
// character regardless — see usAsciiFromEvent() below for how Caps Lock is
// handled on top of this (also without ever reading `.key`).
//
// This map is used ONLY to build this component's own internal scan buffer
// — it is never written back to the DOM or to any input's value, so it has
// zero effect on what a human actually types anywhere (Arabic layout
// included). That is what keeps this fix scoped to the scanner instead of
// becoming a global keyboard remapping.
const US_LAYOUT_CODE_MAP = {
  KeyA: ['a', 'A'], KeyB: ['b', 'B'], KeyC: ['c', 'C'], KeyD: ['d', 'D'],
  KeyE: ['e', 'E'], KeyF: ['f', 'F'], KeyG: ['g', 'G'], KeyH: ['h', 'H'],
  KeyI: ['i', 'I'], KeyJ: ['j', 'J'], KeyK: ['k', 'K'], KeyL: ['l', 'L'],
  KeyM: ['m', 'M'], KeyN: ['n', 'N'], KeyO: ['o', 'O'], KeyP: ['p', 'P'],
  KeyQ: ['q', 'Q'], KeyR: ['r', 'R'], KeyS: ['s', 'S'], KeyT: ['t', 'T'],
  KeyU: ['u', 'U'], KeyV: ['v', 'V'], KeyW: ['w', 'W'], KeyX: ['x', 'X'],
  KeyY: ['y', 'Y'], KeyZ: ['z', 'Z'],
  Digit0: ['0', ')'], Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'],
  Digit4: ['4', '$'], Digit5: ['5', '%'], Digit6: ['6', '^'], Digit7: ['7', '&'],
  Digit8: ['8', '*'], Digit9: ['9', '('],
  Slash: ['/', '?'], Period: ['.', '>'], Comma: [',', '<'],
  Minus: ['-', '_'], Equal: ['=', '+'],
  Semicolon: [';', ':'], Quote: ["'", '"'],
  BracketLeft: ['[', '{'], BracketRight: [']', '}'],
  Backslash: ['\\', '|'], Backquote: ['`', '~'],
  Space: [' ', ' '],
};

function usAsciiFromEvent(e) {
  const pair = US_LAYOUT_CODE_MAP[e.code];
  if (!pair) return null;

  // CAPS LOCK correctness: real USB HID keyboard-wedge scanners default to
  // "Caps Lock Autodetect" firmware — they read the host's Caps Lock LED and
  // adjust their own Shift emission so the OS still outputs the exact target
  // character via the standard keyboard rule `isUpper = shiftKey XOR capsLock`
  // (letters only — digits/symbols are never affected by Caps Lock on a real
  // keyboard). Reading only `e.shiftKey` (as before) silently inverted every
  // letter's case whenever the operator's OS happened to have Caps Lock on.
  // Applying the SAME XOR here — but against `e.code` instead of `e.key` —
  // reproduces the correct character regardless of Caps Lock state without
  // ever touching the layout-dependent `.key` (which is what keeps an Arabic
  // layout from turning this into Arabic characters).
  const isLetter = e.code.startsWith('Key');
  const capsLockOn = isLetter && typeof e.getModifierState === 'function' && e.getModifierState('CapsLock');
  const isUpper = capsLockOn ? !e.shiftKey : e.shiftKey;
  return isUpper ? pair[1] : pair[0];
}

export default {
  name: 'BarcodeScanner',

  props: {
    mode: { type: String, required: true },
    // The consuming page keeps this true through the payment action. This
    // extends the scanner's own resolve lock across the complete operation.
    processing: { type: Boolean, default: false },
  },

  emits: ['scanned', 'scan-error'],

  data() {
    return {
      cameraScanning: false,
      scanner: null,
      scannerDivId: `bc-reader-${Math.random().toString(36).substr(2, 9)}`,
      errorMsg: '',
      flashActive: false,
      lastScanned: '',

      keyBuffer:   [],
      lastKeyTime: 0,
      bufferTimer: null,
      // True once a burst has been recognized as "mid-scan" (2nd+ character
      // arriving within SCAN_BURST_GAP_MS of the previous one) — used to
      // preventDefault()/blur only once per burst, never on a lone keystroke
      // that might just be normal typing.
      burstActive: false,

      isProcessingScan: false,
      recentScanTimes: {},
      lastAcceptedScanValue: null,
      DEBOUNCE_MS: 2000,

      // A USB HID keyboard-wedge scanner sends its whole payload in a few
      // milliseconds per character — far faster than any human can type.
      // MIN_SCAN_LENGTH filters out stray 1-2 key noise; SCAN_BURST_GAP_MS is
      // the max gap allowed between two keystrokes for them to still count as
      // part of the same scanner burst (well below realistic human typing
      // speed, so normal typing in a focused field never accumulates a run
      // long/fast enough to be mistaken for a scan). 100ms (was 40ms) gives
      // real scanners/OS event-loop jitter enough room that a single slow
      // tick mid-transmission no longer looks like a typing pause.
      MIN_SCAN_LENGTH: 3,
      SCAN_BURST_GAP_MS: 100,
      SCAN_IDLE_FLUSH_MS: 200,
    };
  },

  methods: {
    // ── USB keyboard scanner ────────────────────────────────────────────────

    // IMPORTANT: a keyboard-wedge scanner injects keystrokes into whichever
    // element already has focus — it does not change document.activeElement.
    // Detection relies on TIMING (SCAN_BURST_GAP_MS/MIN_SCAN_LENGTH above),
    // not on what currently has focus, so it works regardless of where the
    // cursor happens to be. From the 2nd character of a recognized burst
    // onward we also preventDefault() + blur the focused element exactly
    // once, so scanner characters can no longer leak into (and contaminate)
    // a visible field such as the "ابحث" search box — see PART 2/11 of the
    // USB-scanner fix. The very first character of a burst is NOT
    // intercepted (we cannot yet tell a scan from normal typing at that
    // point), so at most one stray keystroke can ever reach a focused field.
    handleKeydown(e) {
      const now = Date.now();

      if (e.key === 'Enter') {
        if (!this.keyBuffer.length) return; // not ours — leave Enter alone elsewhere
        e.preventDefault();
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        this.burstActive = false;
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''), 'usb');
        return;
      }

      // Layout-independent character for OUR OWN buffer only — never written
      // back to the DOM, so this cannot affect what a human types anywhere,
      // Arabic layout included (see usAsciiFromEvent's doc comment above).
      const char = usAsciiFromEvent(e) ?? (e.key.length === 1 ? e.key : null);
      if (char === null) return;

      // A gap too large to be part of the same scanner burst arrived.
      // Finalize whatever was accumulated so far instead of silently
      // discarding it — silent discard on a slow tick was the exact cause of
      // scans arriving truncated to only their last few characters.
      if (this.keyBuffer.length && (now - this.lastKeyTime) > this.SCAN_BURST_GAP_MS) {
        clearTimeout(this.bufferTimer);
        const buffered = this.keyBuffer.splice(0);
        this.burstActive = false;
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''), 'usb');
      }

      if (this.keyBuffer.length) {
        // Already mid-burst (this is at least the 2nd character): it
        // belongs to the scanner, not to whatever element happens to be
        // focused.
        e.preventDefault();
        if (!this.burstActive) {
          this.burstActive = true;
          const active = document.activeElement;
          if (active && active !== document.body && typeof active.blur === 'function') {
            active.blur();
          }
        }
      }

      this.keyBuffer.push(char);
      this.lastKeyTime = now;

      clearTimeout(this.bufferTimer);
      this.bufferTimer = setTimeout(() => {
        const buffered = this.keyBuffer.splice(0);
        this.burstActive = false;
        if (buffered.length >= this.MIN_SCAN_LENGTH) this.processScanned(buffered.join(''), 'usb');
      }, this.SCAN_IDLE_FLUSH_MS);
    },

    // ── Parse & emit ────────────────────────────────────────────────────────

    // Printed barcodes (BarcodeLabel.vue, legacy) encode "STU-<id>"; older
    // printed QR codes (students_qr.vue / students_qr_print.vue) encode the
    // bare numeric id. Current Student Barcodes QR codes encode a full URL —
    // https://mobile.tarekmath.com/qr/{token} — the opaque token is resolved
    // to a student server-side (never exposes the id/phone client-side). A
    // bare token (no /qr/ prefix — Reservation::generateUniqueQrToken()'s
    // exact Str::random(40) shape) resolves the same way. Anything else
    // (reservation code, free text, ...) is passed through as `raw` for the
    // consumer to interpret, PROVIDED it actually looks like plausible ASCII
    // scanner output — see the validity gate below.
    //
    // `source` ('usb' | 'camera') is informational only — both acquisition
    // paths funnel into this same single normalization/lookup pipeline.
    async processScanned(raw, source = 'usb') {
      console.log('[BarcodeScanner] QR/barcode detected', { source });

      const value = String(raw ?? '').trim();
      if (!value) return;

      console.log('[BarcodeScanner] Decoded value:', value);

      if (this.isProcessingScan || this.processing) {
        console.log('[BarcodeScanner] Scan ignored while another scan/action is processing');
        return;
      }

      const now = Date.now();
      const lastSeenAt = this.recentScanTimes[value] ?? 0;
      if (now - lastSeenAt < this.DEBOUNCE_MS) {
        console.log('[BarcodeScanner] Duplicate scan ignored (within debounce window):', value);
        return;
      }
      for (const [scanValue, scannedAt] of Object.entries(this.recentScanTimes)) {
        if (now - scannedAt >= this.DEBOUNCE_MS) delete this.recentScanTimes[scanValue];
      }
      this.recentScanTimes[value] = now;
      this.lastAcceptedScanValue = value;
      this.isProcessingScan = true;
      this.errorMsg = '';

      try {
        const stuMatch = value.match(/^STU-(\d+)$/i);
        const qrUrlMatch = value.match(/\/qr\/([A-Za-z0-9]+)\/?(?:[?#].*)?$/);
        const bareTokenMatch = /^[A-Za-z0-9]{40}$/.test(value);

        if (stuMatch || (!qrUrlMatch && !bareTokenMatch && /^\d+$/.test(value))) {
          const studentId = stuMatch ? stuMatch[1] : value;
          this.flashScan(studentId ? `STU-${studentId}` : value);
          this.$emit('scanned', { raw: value, studentId, source, mode: this.mode });
          return;
        }

        if (qrUrlMatch || bareTokenMatch) {
          const token = qrUrlMatch ? qrUrlMatch[1] : value;
          this.flashScan(value);

          // Both attendance and payment register the token server-side in the
          // SAME request (POST /attendance/qr / POST /payments/qr-mark-paid
          // each resolve the QR internally) — so this component must NEVER
          // call GET /qr/resolve/{token} itself; that would put two requests
          // on the network for one physical scan. The consuming page is
          // responsible for resolving+acting on the raw token it receives here.
          this.$emit('scanned', { raw: value, qrToken: token, studentId: null, source, mode: this.mode });
          return;
        }

        // Not a recognized id/URL/token shape. Distinguish "clearly not
        // scanner data at all" (non-ASCII — e.g. an Arabic-layout leak, or
        // the scanner-ready status text, or any other stray input) from "a
        // genuine free-text value worth asking the backend about" (e.g. a
        // reservation code). Only the latter reaches the consumer as a real
        // scan; the former is rejected right here, BEFORE any network call,
        // so a misleading "student not found" can never appear for input
        // that was never actually looked up (see PART 9 of the fix).
        const looksLikePlausibleScannerText = /^[\x20-\x7E]{3,80}$/.test(value);
        if (!looksLikePlausibleScannerText) {
          console.log('[BarcodeScanner] Rejected non-ASCII/garbled scan value (no request sent):', value);
          this.errorMsg = 'قيمة الماسح غير صالحة — أعد المحاولة';
          this.$emit('scan-error', {
            message: 'قيمة الماسح غير صالحة — أعد المحاولة',
            raw: value,
            source,
            mode: this.mode,
            invalid: true,
          });
          return;
        }

        this.flashScan(value);
        this.$emit('scanned', { raw: value, studentId: null, source, mode: this.mode });
      } finally {
        this.isProcessingScan = false;
      }
    },

    flashScan(label) {
      this.lastScanned  = label;
      this.flashActive  = true;
      setTimeout(() => { this.flashActive = false; }, 600);
    },

    // ── Camera ──────────────────────────────────────────────────────────────

    async toggleCamera() {
      this.cameraScanning ? await this.stopCamera() : await this.startCamera();
    },

    async startCamera() {
      this.errorMsg      = '';
      this.cameraScanning = true;
      await this.$nextTick();

      try {
        this.scanner = new Html5Qrcode(this.scannerDivId);
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 350, height: 100 } },
          (decoded) => this.processScanned(decoded, 'camera'),
          () => {}
        );
        console.log('[BarcodeScanner] Scanner initialized (camera)');
      } catch {
        this.errorMsg      = 'تعذّر الوصول إلى الكاميرا. يرجى السماح بالإذن وإعادة المحاولة.';
        this.cameraScanning = false;
      }
    },

    async stopCamera() {
      if (this.scanner?.isScanning) {
        try { await this.scanner.stop(); } catch {}
      }
      this.scanner       = null;
      this.cameraScanning = false;
    },
  },

  watch: {
    // Restart the cooldown when the consuming action finishes, so the camera's
    // next frame cannot immediately resolve the same token again.
    processing(value, previous) {
      if (!value && previous && this.lastAcceptedScanValue) {
        // Attendance relies on the processing lock while its request is active,
        // then becomes ready immediately. Payment keeps its short post-action
        // cooldown to suppress repeated camera frames for the same receipt.
        if (this.mode === 'attendance') delete this.recentScanTimes[this.lastAcceptedScanValue];
        else this.recentScanTimes[this.lastAcceptedScanValue] = Date.now();
      }
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    console.log('[BarcodeScanner] Scanner initialized (USB keyboard-wedge)', { mode: this.mode });
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.bufferTimer);
    this.stopCamera();
  },
};
</script>

<style scoped>
.barcode-scanner-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
}

.usb-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #15803d;
  transition: background 0.3s;
}

.usb-indicator.usb-flash {
  background: #dcfce7;
  border-color: #22c55e;
}

.usb-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.camera-viewport {
  border: 2px solid #0d6efd;
  border-radius: 8px;
  overflow: hidden;
  max-width: 400px;
}
</style>
